import React, { PureComponent } from 'react';
import { Flex } from '../../../common/Flex';
import { FlowGrid } from './FlowGrid';
import { FlowSectionHeader } from './FlowSectionHeader';
import { TVLoad } from './TVLoad';
import { ExtendedMonitoredTv, MonitoredEvent, MonitoredMeasure, TvMeteoAlert } from 'app/model/SOL49';
import { DEFAULT_FLOW_SETTINGS, FlowSettings, FlowSettingsContent, FlowSettingsTitle } from './settings/FlowSettings';
import { SettingFilled } from '@ant-design/icons';
import { ClosablePopover } from '../../../common/ClosablePopover';
import { SortUtils } from 'app/common/util/SortUtils';
import MonitoredLoadUtils from '../../../../common/util/MonitoredLoadUtils';
import MeasureUtils from '../../../../common/util/MeasureUtils';
import { FlightId, TimePeriod } from 'app/model/CORE';
import { IFlowSection } from './IFlowSection';
import Utils from '../../../../common/util/Utils';
import { MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import {
    changeFlowsSectionTitle,
    deleteFlowSection,
    moveFlowSectionDown,
    moveFlowSectionUp,
} from '../../../../redux/features/dashboard/user-monitored-flows-slice';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from '../../../../redux/store';

const mapStateToProps = (state: RootState) => ({});
const mapDispatchToProps = {
    changeFlowsSectionTitle,
    moveFlowSectionUp,
    moveFlowSectionDown,
    deleteFlowSection,
};

const connector = connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true });
type PropsFromRedux = ConnectedProps<typeof connector>;

interface FlowSectionProps extends PropsFromRedux {
    uuid: number;
    title: string;

    monitoredTvList?: Array<ExtendedMonitoredTv>;
    tvSetMap?: { [key: string]: string };
    currentTime: Date;
    monitoredMeasures?: Array<MonitoredMeasure>;
    meteoAlerts?: { [key: string]: Array<TvMeteoAlert> };
    events: MonitoredEvent[];
    tvsOccupancyPeriods?: { [key: string]: Map<FlightId, TimePeriod> };
    nbColumns: number;

    moveUpEnabled: boolean;
    moveDownEnabled: boolean;
    selectedTvs?: ExtendedMonitoredTv[];

    onSelectionChange?(tvs: ExtendedMonitoredTv[], selected: boolean): void;
}

interface FlowSectionState {
    folded: boolean; // Fold/unfold feature
    settings: FlowSettings;
}

export class FlowSectionComponent extends PureComponent<FlowSectionProps, FlowSectionState> implements IFlowSection {
    private settingsPopoverRef = React.createRef<ClosablePopover>();

    state = {
        folded: false,
        settings: DEFAULT_FLOW_SETTINGS,
    };

    getDisplayedMonitoredTvs = (): Array<ExtendedMonitoredTv> => {
        return this.getFilteredMonitoredTvList(this.props.monitoredTvList, this.state.settings);
    };

    handleSelectionChange = (tv: ExtendedMonitoredTv, selected: boolean) => {
        this.props.onSelectionChange?.([tv], selected);
    };

    handleSettingsChange = (settings: FlowSettings) => {
        this.setState({ settings: settings }, () => this.settingsPopoverRef.current.hide());
    };

    handleFoldedChange = (folded: boolean) => {
        this.setState({ folded: folded });
    };

    getFilteredMonitoredTvList = (monitoredTvList: Array<ExtendedMonitoredTv>, settings: FlowSettings) => {
        return monitoredTvList?.filter(monitoredTv =>
            MonitoredLoadUtils.isEntryDisplayed(monitoredTv.loadValues, settings.tv),
        );
    };

    renderContent = () => {
        if (this.state.folded) return undefined;

        // Filter data based on display settings
        const filteredMonitoredTvList = this.getFilteredMonitoredTvList(
            this.props.monitoredTvList,
            this.state.settings,
        );
        return (
            <Flex>
                <FlowGrid columns={this.props.nbColumns}>
                    {filteredMonitoredTvList?.sort(SortUtils.MONITORED_TV_ALPHABETICAL_SORTING).map(monitoredTv => {
                        const tvId = monitoredTv.refLocation.id;
                        const tvMeasures = this.props.monitoredMeasures?.filter(
                            monitoredMeasure => tvId === MeasureUtils.getTvLocation(monitoredMeasure.measure).id,
                        );
                        const occupancyPeriods = this.props.tvsOccupancyPeriods?.[tvId];
                        const events = this.props.events?.filter(
                            e =>
                                (MonitoredEventUtils.isWeatherEvent(e) &&
                                    e.refLocs?.some(refLoc =>
                                        Utils.isShallowEquals(refLoc, monitoredTv.tvRefLocation),
                                    )) ||
                                (MonitoredEventUtils.isNoteEvent(e) && e.tvs?.includes(tvId)),
                        );
                        return (
                            <TVLoad
                                key={tvId}
                                flowUUID={this.props.uuid}
                                tvName={tvId}
                                vsName={this.props.tvSetMap[tvId]}
                                elementary={monitoredTv.elementary}
                                entryLoad={monitoredTv.loadValues}
                                occupancyLoad={monitoredTv.occupancyLoadValues}
                                monitoredMeasures={tvMeasures}
                                activePeriods={monitoredTv.activePeriods}
                                showLoadInactivePeriods={this.state.settings.showLoadInactivePeriods}
                                occupancyPeriods={occupancyPeriods}
                                currentTime={this.props.currentTime}
                                monitorable={monitoredTv.monitorable}
                                selected={this.props.selectedTvs?.indexOf(monitoredTv) >= 0}
                                onSelectionChange={selected => this.handleSelectionChange(monitoredTv, selected)}
                                displayOccupancy={
                                    !this.state.settings.showOccupancy ? 'none' : this.state.settings.occupancy
                                }
                                displayOccupancyForTv={
                                    !this.state.settings.showOccupancyForTv ? 'all' : this.state.settings.occupancyForTv
                                }
                                tvMeteoAlerts={this.props.meteoAlerts?.[tvId]}
                                events={events}
                            />
                        );
                    })}
                </FlowGrid>
            </Flex>
        );
    };

    render() {
        const tvSets = Utils.removeDuplicates(
            this.props.monitoredTvList?.map(monitoredTv => this.props.tvSetMap[monitoredTv.refLocation.id]),
        );
        return (
            <Flex direction="vertical">
                <div className="on-hover" style={{ position: 'relative' }}>
                    <FlowSectionHeader
                        title={this.props.title}
                        onTitleChange={(newTitle: string) => {
                            this.props.changeFlowsSectionTitle({ flowUuid: this.props.uuid, newTitle: newTitle });
                        }}
                        folded={this.state.folded}
                        onFoldedChange={this.handleFoldedChange}
                        nbTvs={this.props.monitoredTvList?.length}
                        nbTvSets={tvSets?.length}
                        moveUpEnabled={this.props.moveUpEnabled}
                        moveDownEnabled={this.props.moveDownEnabled}
                        deleteEnabled={true}
                        onMoveUp={() => this.props.moveFlowSectionUp(this.props.uuid)}
                        onMoveDown={() => this.props.moveFlowSectionDown(this.props.uuid)}
                        onDeleteSection={() => this.props.deleteFlowSection(this.props.uuid)}
                        extraActions={[
                            <ClosablePopover
                                key="settings-action"
                                ref={this.settingsPopoverRef}
                                trigger="click"
                                placement="bottom"
                                title={<FlowSettingsTitle flowName={this.props.title} />}
                                content={
                                    <FlowSettingsContent
                                        settings={this.state.settings}
                                        onChange={this.handleSettingsChange}
                                        onClose={() => this.settingsPopoverRef.current.hide()}
                                    />
                                }>
                                <SettingFilled onClick={() => this.settingsPopoverRef.current.show()} />
                            </ClosablePopover>,
                        ]}
                    />
                </div>
                {this.renderContent()}
            </Flex>
        );
    }
}

export const FlowSection = connector(FlowSectionComponent);
